import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Lock, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";

export const TOKEN_KEY = "cs_celebrations_admin";

export function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/celebrations/admin/auth", { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md mx-auto bg-white border border-[#E5E1DA] p-10" data-testid="celebrations-admin-login">
      <Lock className="text-[#d4a373]" size={28} />
      <h1 className="mt-4 text-3xl text-[#1e4d5d]" style={{ fontFamily: "Playfair Display, serif" }}>Team Admin</h1>
      <p className="mt-2 text-sm text-[#2c5865]/80">Team only. Enter the password to manage events and celebrations.</p>
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        className="mt-6 w-full border border-[#E5E1DA] px-4 py-3 outline-none focus:border-[#d4a373]"
        data-testid="celebrations-admin-password"
        required
      />
      <button className="btn-primary mt-4 w-full" disabled={loading} data-testid="celebrations-admin-login-btn">
        {loading ? "Checking…" : "Unlock"}
      </button>
    </form>
  );
}

function UploadForm({ token, onUploaded }) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a graphic to upload");
    const fd = new FormData();
    fd.append("name", name);
    fd.append("message", message);
    fd.append("file", file);
    setLoading(true);
    setProgress(0);
    try {
      await api.post("/celebrations", fd, {
        headers: { "Content-Type": "multipart/form-data", "X-Admin-Token": token },
        onUploadProgress: (p) => p.total && setProgress(Math.round((p.loaded / p.total) * 100)),
      });
      toast.success(`Posted ${name}'s celebration!`);
      setName(""); setMessage(""); setFile(null);
      e.target.reset();
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full border border-[#E5E1DA] px-4 py-3 outline-none focus:border-[#d4a373] bg-white";
  return (
    <form onSubmit={submit} className="bg-white border border-[#E5E1DA] p-5 md:p-8 space-y-4 min-w-0" data-testid="celebrations-upload-form">
      <h2 className="text-2xl text-[#1e4d5d]" style={{ fontFamily: "Playfair Display, serif" }}>Add a celebration</h2>
      <input className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="Friend's name" required data-testid="celebrations-upload-name" />
      <textarea className={field} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Short message" data-testid="celebrations-upload-message" />
      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files[0])} className="block w-full max-w-full text-sm" data-testid="celebrations-upload-file" />
      {loading && (
        <div className="h-1 bg-[#E5E1DA]" data-testid="celebrations-upload-progress">
          <div className="h-1 bg-[#d4a373] transition-[width]" style={{ width: `${progress}%` }} />
        </div>
      )}
      <button className="btn-primary flex items-center gap-2" disabled={loading} data-testid="celebrations-upload-submit">
        <Upload size={16} /> {loading ? `Uploading ${progress}%` : "Post celebration"}
      </button>
    </form>
  );
}

export function CelebrationsManager({ token, onUnauthorized }) {
  const [items, setItems] = useState([]);

  const load = useCallback(() => api.get("/celebrations").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}'s celebration?`)) return;
    try {
      await api.delete(`/celebrations/${c.id}`, { headers: { "X-Admin-Token": token } });
      toast.success("Removed");
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      toast.error("Could not remove");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[420px_1fr] [&>*]:min-w-0" data-testid="celebrations-manager">
      <UploadForm token={token} onUploaded={load} />
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3 content-start" data-testid="celebrations-admin-list">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-[#E5E1DA]" data-testid={`celebrations-admin-item-${c.id}`}>
            <img src={`${API}/celebrations/${c.id}/image`} alt={c.name} className="w-full aspect-[4/5] object-cover" />
            <div className="p-4 flex items-center justify-between">
              <span className="text-[#1e4d5d] font-medium">{c.name}</span>
              <button onClick={() => remove(c)} aria-label="Remove" className="text-[#2c5865] hover:text-red-700" data-testid={`celebrations-admin-delete-${c.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CelebrationsAdmin() {
  return <Navigate to="/admin?tab=celebrations" replace />;
}
